import { useAccountScope } from '../context/AccountScope';
import isMobileLayout from '../utils/isMobileLayout';
import { useEffect, useState } from 'react';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';

function UsersPage() {
  const { fetch } = useAccountScope();
  const [isMobile, setIsMobile] =
    useState(isMobileLayout);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isMobileLayout());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const [usuarios, setUsuarios] =
    useState([]);

  const [busquedaNombre, setBusquedaNombre] =
    useState('');

  const [busquedaCorreo, setBusquedaCorreo] =
    useState('');

  const [filtroTipo, setFiltroTipo] =
    useState('todas');

  const [modalCrear, setModalCrear] =
    useState(false);

  const [modalEditar, setModalEditar] =
    useState(false);

  const [
    usuarioSeleccionado,
    setUsuarioSeleccionado
  ] = useState(null);

  const hierarchy = {
    super_admin: 4,
    admin_empresa: 3,
    administrador: 2,
    usuario: 1
  };

  const [currentUser, setCurrentUser] = useState({});
  const [errorCarga, setErrorCarga] = useState('');
  const [cuentaAbierta, setCuentaAbierta] = useState(null);

  function canCreateUsers() {
    return [
      'super_admin',
      'admin_empresa',
      'administrador'
    ].includes(currentUser.role);
  }

  function canManageUser(usuario) {
    if (!currentUser || !usuario) {
      return false;
    }

    if (
      !hierarchy[currentUser.role] ||
      !hierarchy[usuario.role]
    ) {
      return false;
    }

    if (
      hierarchy[usuario.role] >=
      hierarchy[currentUser.role]
    ) {
      return false;
    }

    if (
      currentUser.role !== 'super_admin' &&
      String(currentUser.company_id) !== String(usuario.company_id)
    ) return false;

    if (currentUser.role === 'administrador') {
      return usuario.role === 'usuario' &&
        String(usuario.parent_user_id) === String(currentUser.id);
    }

    return true;
  }

  useEffect(() => {
    cargarUsuarios();
  }, []);

  async function cargarUsuarios() {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };
      const [usuariosResponse, meResponse] = await Promise.all([
        fetch('/api/users', { headers }),
        fetch('/api/auth/my-account', { headers })
      ]);
      if (!usuariosResponse.ok || !meResponse.ok) {
        throw new Error('No se pudieron cargar las cuentas');
      }
      const [data, meData] = await Promise.all([
        usuariosResponse.json(), meResponse.json()
      ]);
      if (!Array.isArray(data) || !meData.account?.role) {
        throw new Error('La respuesta de cuentas no es válida');
      }
      setCurrentUser(meData.account);
      setUsuarios(data);
      setErrorCarga('');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error(error);
      setErrorCarga(error.message);
    }
  }

  function traducirRol(role) {
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'admin_empresa') return 'Empresa';
    if (role === 'administrador') return 'Administrador';
    if (role === 'usuario') return 'Usuario';

    return role;
  }

  function normalizarTipo(role) {
    if (role === 'admin_empresa') return 'empresas';
    if (role === 'administrador') return 'administradores';
    if (role === 'usuario') return 'usuarios';

    return 'otros';
  }

  const cuentasFiltradas =
    usuarios.filter((usuario) => {
      const nombreOk =
        usuario.name
          ?.toLowerCase()
          .includes(
            busquedaNombre.toLowerCase()
          );

      const correoOk =
        usuario.email
          ?.toLowerCase()
          .includes(
            busquedaCorreo.toLowerCase()
          );

      const tipoUsuario =
        normalizarTipo(usuario.role);

      const tipoOk =
        filtroTipo === 'todas'
          ? true
          : tipoUsuario === filtroTipo;

      const rolValido = [
        'super_admin',
        'admin_empresa',
        'administrador',
        'usuario'
      ].includes(usuario.role);

      return (
        nombreOk &&
        correoOk &&
        tipoOk &&
        rolValido
      );
    });

  const superadministradores =
    cuentasFiltradas.filter(
      (u) => u.role === 'super_admin'
    );

  const empresas =
    cuentasFiltradas.filter(
      (u) => u.role === 'admin_empresa'
    );

  const administradores =
    cuentasFiltradas.filter(
      (u) => u.role === 'administrador'
    );

  const usuariosFinales =
    cuentasFiltradas.filter(
      (u) => u.role === 'usuario'
    );

  function getFilterStyle(tipo) {
    if (filtroTipo === tipo) {
      return isMobile
        ? filterActiveMobile
        : filterActive;
    }

    return isMobile
      ? filterButtonMobile
      : filterButton;
  }

  function renderTabla(titulo, data) {
    if (data.length === 0) {
      return null;
    }

    return (
      <div style={isMobile ? sectionBoxMobile : sectionBox}>
        <h3 style={isMobile ? sectionTitleMobile : sectionTitle}>
          {titulo}
        </h3>

        {isMobile ? <div style={{ display: 'grid', gap: 10 }}>
          {data.map(usuario => <article key={usuario.id}
            style={{ padding: 14, border: '1px solid #e2e8f0', borderRadius: 14,
              background: '#fff', minWidth: 0, overflowWrap: 'anywhere' }}>
            <strong>{usuario.name}</strong>
            <div style={{ color: '#64748b', fontSize: 13, margin: '5px 0' }}>
              {usuario.email}
            </div>
            <div style={{ fontSize: 13 }}>
              {traducirRol(usuario.role)} · {usuario.status}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 5 }}>
              Responsable: {usuarios.find(u => String(u.id) ===
                String(usuario.parent_user_id))?.name ||
                (String(usuario.parent_user_id) === String(currentUser.id)
                  ? currentUser.name : '—')}
            </div>
            <div style={{ marginTop: 10 }}>
              <button type="button" onClick={() => setCuentaAbierta(usuario)}
                style={smallButton}>Ver cuenta</button>{' '}
              {canManageUser(usuario) && <button type="button" style={smallButton}
                onClick={() => { setUsuarioSeleccionado(usuario); setModalEditar(true); }}>
                Editar
              </button>}
            </div>
          </article>)}
        </div> : <div style={tableScroll}>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeadRow}>
                <th style={thStyle}>Nombre</th>
                <th style={thStyle}>Correo</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Responsable</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {data.map((usuario) => (
                <tr key={usuario.id}>
                  <td style={tdStyle}>
                    <strong>
                      {usuario.name}
                    </strong>
                  </td>

                  <td style={tdStyle}>
                    {usuario.email}
                  </td>

                  <td style={tdStyle}>
                    {traducirRol(usuario.role)}
                  </td>

                  <td style={tdStyle}>
                    {usuarios.find(u => String(u.id) ===
                      String(usuario.parent_user_id))?.name ||
                      (String(usuario.parent_user_id) === String(currentUser.id)
                        ? currentUser.name : '—')}
                  </td>

                  <td style={tdStyle}>
                    <span
                      style={
                        usuario.status === 'active'
                          ? activeBadge
                          : usuario.status === 'suspended'
                            ? suspendedBadge
                            : inactiveBadge
                      }
                    >
                      {usuario.status}
                    </span>
                  </td>

                  <td style={tdStyle}>
                    <button type="button" onClick={() => setCuentaAbierta(usuario)}
                      style={smallButton}>Ver cuenta</button>{' '}
                    {canManageUser(usuario) ? (
                      <button
                        onClick={() => {
                          setUsuarioSeleccionado(usuario);
                          setModalEditar(true);
                        }}
                        style={smallButton}
                      >
                        Editar
                      </button>
                    ) : (
                      <span style={noActionText}>
                        Sin acciones
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>
    );
  }

  return (
    <div style={isMobile ? wrapperMobile : wrapper}>
      <div style={isMobile ? headerMobile : header}>
        <div>
          <h2 style={isMobile ? titleMobile : title}>
            Gestión de cuentas
          </h2>

          <p style={isMobile ? subtitleMobile : subtitle}>
            Administra empresas, administradores,
            usuarios y accesos según la jerarquía
            permitida.
          </p>
        </div>

        {canCreateUsers() && (
          <button
            onClick={() =>
              setModalCrear(true)
            }
            style={isMobile ? primaryButtonMobile : primaryButton}
          >
            + Nueva cuenta
          </button>
        )}
      </div>

      <div style={isMobile ? filtersBoxMobile : filtersBox}>
        <input
          type="text"
          placeholder="Buscar por nombre..."
          value={busquedaNombre}
          onChange={(e) =>
            setBusquedaNombre(e.target.value)
          }
          style={inputStyle}
        />

        <input
          type="text"
          placeholder="Buscar por correo..."
          value={busquedaCorreo}
          onChange={(e) =>
            setBusquedaCorreo(e.target.value)
          }
          style={inputStyle}
        />
      </div>

      <div style={isMobile ? filterButtonsMobile : filterButtons}>
        <button
          onClick={() =>
            setFiltroTipo('todas')
          }
          style={getFilterStyle('todas')}
        >
          Todas
        </button>

        {currentUser.role === 'super_admin' && (
          <button
            onClick={() =>
              setFiltroTipo('empresas')
            }
            style={getFilterStyle('empresas')}
          >
            Empresas
          </button>
        )}

        {(currentUser.role === 'super_admin' ||
          currentUser.role === 'admin_empresa') && (
          <button
            onClick={() =>
              setFiltroTipo('administradores')
            }
            style={getFilterStyle('administradores')}
          >
            Administradores
          </button>
        )}

        <button
          onClick={() =>
            setFiltroTipo('usuarios')
          }
          style={getFilterStyle('usuarios')}
        >
          Usuarios
        </button>
      </div>

      {errorCarga && (
        <div role="alert" style={emptyState}>{errorCarga}</div>
      )}

      {cuentasFiltradas.length === 0 && !errorCarga && (
        <div style={isMobile ? emptyStateMobile : emptyState}>
          No hay cuentas para mostrar.
        </div>
      )}

      {currentUser.role === 'super_admin' &&
        renderTabla('Superadministración', superadministradores)}

      {currentUser.role === 'super_admin' &&
        renderTabla('Empresas', empresas)}

      {(currentUser.role === 'super_admin' ||
        currentUser.role === 'admin_empresa') &&
        renderTabla(
          'Administradores',
          administradores
        )}

      {renderTabla(
        'Usuarios',
        usuariosFinales
      )}

      {cuentaAbierta && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(15,23,42,.65)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: 16
        }} onClick={() => setCuentaAbierta(null)}>
          <div role="dialog" aria-modal="true" aria-label="Detalle de cuenta"
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', color: '#0f172a',
              padding: 24, borderRadius: 16,
              width: '100%', maxWidth: 520,
              maxHeight: '80vh', overflowY: 'auto'
            }}>
            <h3 style={{ marginTop: 0 }}>Cuenta de {cuentaAbierta.name}</h3>
            <p><strong>Rol:</strong> {traducirRol(cuentaAbierta.role)}</p>
            <p><strong>Correo:</strong> {cuentaAbierta.email}</p>
            <p><strong>Estado:</strong> {cuentaAbierta.status}</p>
            <p><strong>Responsable:</strong>{' '}
              {usuarios.find(u => String(u.id) ===
                String(cuentaAbierta.parent_user_id))?.name || '—'}
            </p>

            <h4>Cuentas directas a cargo</h4>
            {usuarios.filter(u => String(u.parent_user_id) ===
              String(cuentaAbierta.id)).map(u => (
                <button key={u.id} type="button"
                  onClick={() => setCuentaAbierta(u)}
                  style={{
                    ...smallButton, display: 'block',
                    marginBottom: 8, color: '#0f172a'
                  }}>
                  Abrir {u.name} · {traducirRol(u.role)}
                </button>
              ))}
            {!usuarios.some(u => String(u.parent_user_id) ===
              String(cuentaAbierta.id)) && <p>No tiene cuentas directas.</p>}

            {usuarios.some(u => String(u.id) ===
              String(cuentaAbierta.parent_user_id)) && (
              <button type="button"
                style={{
                  ...smallButton, marginRight: 8,
                  color: '#0f172a'
                }}
                onClick={() => setCuentaAbierta(usuarios.find(u =>
                  String(u.id) === String(cuentaAbierta.parent_user_id)))}>
                ← Volver al responsable
              </button>
            )}
            <button type="button"
              style={{ ...smallButton, color: '#0f172a' }}
              onClick={() => setCuentaAbierta(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      <CreateUserModal
        abierto={modalCrear}
        setAbierto={setModalCrear}
      />

      <EditUserModal
        abierto={modalEditar}
        setAbierto={setModalEditar}
        usuario={usuarioSeleccionado}
      />
    </div>
  );
}

const wrapper = {
  flex: 1,
  background: '#f8fafc',
  color: '#111827',
  padding: '24px 40px 40px',
  overflowY: 'auto'
};

const wrapperMobile = {
  ...wrapper,
  padding: '18px 14px 90px',
  overflowX: 'hidden',
  boxSizing: 'border-box'
};

const header = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
};

const headerMobile = {
  ...header,
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '14px',
  marginBottom: '18px'
};

const title = {
  margin: 0,
  fontSize: '24px',
  color: '#0f172a'
};

const titleMobile = {
  ...title,
  fontSize: '22px',
  lineHeight: '1.15'
};

const subtitle = {
  marginTop: '8px',
  color: '#64748b',
  maxWidth: '720px'
};

const subtitleMobile = {
  ...subtitle,
  fontSize: '13px',
  lineHeight: '1.4',
  maxWidth: '100%'
};

const primaryButton = {
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '12px 20px',
  cursor: 'pointer',
  fontWeight: '700'
};

const primaryButtonMobile = {
  ...primaryButton,
  width: '100%',
  borderRadius: '14px',
  padding: '13px 16px'
};

const filtersBox = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '20px',
  marginBottom: '14px',
  boxShadow:
    '0 2px 8px rgba(0,0,0,0.05)',
  display: 'grid',
  gridTemplateColumns:
    'repeat(2, minmax(220px, 1fr))',
  gap: '14px'
};

const filtersBoxMobile = {
  ...filtersBox,
  gridTemplateColumns: '1fr',
  padding: '14px',
  gap: '10px',
  borderRadius: '16px'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  outline: 'none',
  fontWeight: '600',
  boxSizing: 'border-box'
};

const filterButtons = {
  display: 'flex',
  gap: '10px',
  marginBottom: '20px'
};

const filterButtonsMobile = {
  ...filterButtons,
  overflowX: 'auto',
  paddingBottom: '8px',
  WebkitOverflowScrolling: 'touch'
};

const filterButton = {
  border: '1px solid #dbeafe',
  background: '#ffffff',
  color: '#1e40af',
  borderRadius: '12px',
  padding: '11px 16px',
  cursor: 'pointer',
  fontWeight: '800'
};

const filterButtonMobile = {
  ...filterButton,
  borderRadius: '999px',
  padding: '10px 14px',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  flex: '0 0 auto'
};

const filterActive = {
  ...filterButton,
  background: '#2563eb',
  color: '#ffffff',
  border: '1px solid #2563eb'
};

const filterActiveMobile = {
  ...filterButtonMobile,
  background: '#2563eb',
  color: '#ffffff',
  border: '1px solid #2563eb'
};

const sectionBox = {
  background: '#ffffff',
  borderRadius: '16px',
  marginBottom: '22px',
  overflow: 'hidden',
  boxShadow:
    '0 2px 8px rgba(0,0,0,0.05)'
};

const sectionBoxMobile = {
  ...sectionBox,
  borderRadius: '16px',
  marginBottom: '18px'
};

const sectionTitle = {
  margin: 0,
  padding: '18px 20px',
  fontSize: '18px',
  color: '#0f172a',
  borderBottom: '1px solid #f1f5f9'
};

const sectionTitleMobile = {
  ...sectionTitle,
  padding: '16px',
  fontSize: '17px'
};

const tableScroll = {
  width: '100%',
  overflowX: 'auto'
};

const tableScrollMobile = {
  ...tableScroll,
  WebkitOverflowScrolling: 'touch'
};

const tableStyle = {
  width: '100%',
  minWidth: '720px',
  borderCollapse: 'collapse'
};

const tableHeadRow = {
  background: '#f8fafc'
};

const thStyle = {
  textAlign: 'left',
  padding: '16px',
  color: '#64748b',
  fontSize: '13px',
  fontWeight: '700'
};

const tdStyle = {
  padding: '16px',
  borderTop: '1px solid #f1f5f9',
  color: '#334155',
  whiteSpace: 'nowrap'
};

const activeBadge = {
  padding: '5px 12px',
  borderRadius: '999px',
  background: '#dcfce7',
  color: '#166534',
  fontSize: '12px',
  fontWeight: '700'
};

const suspendedBadge = {
  padding: '5px 12px',
  borderRadius: '999px',
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '12px',
  fontWeight: '700'
};

const inactiveBadge = {
  padding: '5px 12px',
  borderRadius: '999px',
  background: '#fee2e2',
  color: '#991b1b',
  fontSize: '12px',
  fontWeight: '700'
};

const smallButton = {
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  color: '#0f172a',
  padding: '8px 12px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '700'
};

const noActionText = {
  color: '#94a3b8',
  fontSize: '13px'
};

const emptyState = {
  background: '#ffffff',
  padding: '22px',
  borderRadius: '16px',
  color: '#64748b',
  fontWeight: '700',
  boxShadow:
    '0 2px 8px rgba(0,0,0,0.05)'
};

const emptyStateMobile = {
  ...emptyState,
  padding: '18px'
};

export default UsersPage;
